import { forwardRef, type HTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react'
import clsx from 'clsx'

/**
 * BentoCard - Carte Bento avec variants de taille
 *
 * Composant de carte flexible avec 5 variants de taille pour s'adapter
 * à différents types de contenu. Supporte les états interactifs et statiques.
 *
 * @example
 * <BentoCard size="lg" onClick={handleClick}>
 *   <BentoCardTitle>Mon Titre</BentoCardTitle>
 *   <BentoCardDescription>Description du contenu</BentoCardDescription>
 * </BentoCard>
 */
export type BentoCardSize = 'sm' | 'md' | 'lg' | 'xl' | 'featured'

type BentoCardDivProps = HTMLAttributes<HTMLDivElement> & {
  onClick?: never
}

type BentoCardButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  onClick: () => void
}

export interface BentoCardBaseProps {
  /** Taille de la carte */
  size?: BentoCardSize
  /** Active les effets hover/focus */
  interactive?: boolean
  /** Contenu de la carte */
  children: ReactNode
}

export type BentoCardProps = BentoCardBaseProps & (BentoCardDivProps | BentoCardButtonProps)

export const BentoCard = forwardRef<HTMLDivElement | HTMLButtonElement, BentoCardProps>(
  ({ size = 'md', interactive = true, children, onClick, className = '', ...rest }, ref) => {
    const sizeClasses: Record<BentoCardSize, string> = {
      sm: 'serge-bento-card-sm',
      md: 'serge-bento-card-md',
      lg: 'serge-bento-card-lg',
      xl: 'serge-bento-card-xl',
      featured: 'serge-bento-card-featured',
    }

    const classes = clsx(
      sizeClasses[size],
      !interactive && 'serge-bento-card-static',
      className
    )

    if (onClick) {
      return (
        <button
          ref={ref as React.Ref<HTMLButtonElement>}
          type="button"
          className={classes}
          onClick={onClick}
          {...(rest as ButtonHTMLAttributes<HTMLButtonElement>)}
        >
          {children}
        </button>
      )
    }

    return (
      <div
        ref={ref as React.Ref<HTMLDivElement>}
        className={classes}
        {...(rest as HTMLAttributes<HTMLDivElement>)}
      >
        {children}
      </div>
    )
  }
)

BentoCard.displayName = 'BentoCard'

/**
 * BentoCardHeader - Section d'en-tête de carte
 *
 * Container flex pour positionner des éléments en haut de la carte
 * (labels, badges, icônes, etc.)
 *
 * @example
 * <BentoCardHeader>
 *   <BentoCardNumber>01</BentoCardNumber>
 *   <Icon />
 * </BentoCardHeader>
 */
export interface BentoCardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode
}

export const BentoCardHeader = forwardRef<HTMLDivElement, BentoCardHeaderProps>(
  ({ children, className = '', ...rest }, ref) => (
    <div ref={ref} className={clsx('serge-bento-card-header', className)} {...rest}>
      {children}
    </div>
  )
)

BentoCardHeader.displayName = 'BentoCardHeader'

/**
 * BentoCardNumber - Badge numérique ou label court
 *
 * Petit label monospace pour afficher des numéros, codes ou badges courts.
 *
 * @example
 * <BentoCardNumber>01</BentoCardNumber>
 * <BentoCardNumber>P1</BentoCardNumber>
 */
export interface BentoCardNumberProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode
}

export const BentoCardNumber = forwardRef<HTMLSpanElement, BentoCardNumberProps>(
  ({ children, className = '', ...rest }, ref) => (
    <span ref={ref} className={clsx('serge-bento-card-number', className)} {...rest}>
      {children}
    </span>
  )
)

BentoCardNumber.displayName = 'BentoCardNumber'

/**
 * BentoCardTitle - Titre de carte
 *
 * Titre principal de la carte avec styling approprié (13px, semibold).
 *
 * @example
 * <BentoCardTitle>Titre de la carte</BentoCardTitle>
 */
export interface BentoCardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode
}

export const BentoCardTitle = forwardRef<HTMLHeadingElement, BentoCardTitleProps>(
  ({ children, className = '', ...rest }, ref) => (
    <h3 ref={ref} className={clsx('serge-bento-card-title', className)} {...rest}>
      {children}
    </h3>
  )
)

BentoCardTitle.displayName = 'BentoCardTitle'

/**
 * BentoCardDescription - Description de carte
 *
 * Texte secondaire pour décrire le contenu de la carte (11px, muted).
 *
 * @example
 * <BentoCardDescription>
 *   Description détaillée du contenu de cette carte.
 * </BentoCardDescription>
 */
export interface BentoCardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {
  children: ReactNode
}

export const BentoCardDescription = forwardRef<HTMLParagraphElement, BentoCardDescriptionProps>(
  ({ children, className = '', ...rest }, ref) => (
    <p ref={ref} className={clsx('serge-bento-card-description', className)} {...rest}>
      {children}
    </p>
  )
)

BentoCardDescription.displayName = 'BentoCardDescription'
